import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppInputText from 'components/app/AppInputText';
import AppInputCheckbox from 'components/app/AppInputCheckbox';
import AppTurnstile from 'components/app/AppTurnstile';
import EulaModal from './EulaModal';
import PrivatePolicyModal from './PrivatePolicyModal';
import AgreePromotionModal from './AgreePromotionModal';
import { BASE_URL } from 'const/ConstBase';
import { SignUpCredentials, SignUpResponse } from 'contexts/AuthContext';
import { isPasswordValid } from 'utils/commonUtils';
import useSession from 'hooks/useSession';

import './RegistAccountBody.scss';

const RegistAccountBody = () => {
    const [email, setEmail] = useState('');
    const [forum_name, setForumName] = useState('');
    const [password, setPassword] = useState('');
    const [confirm_password, setConfirmPassword] = useState('');
    const [agree_eula, setAgreeEula] = useState(true);
    const [agree_pp, setAgreePp] = useState(true);
    const [allow_email, setAllowEmail] = useState(true);
    const [captchaToken, setCaptchaToken] = useState('');

    const [err_email, setErrEmail] = useState('');
    const [err_forum_name, setErrForumName] = useState('');
    const [err_password, setErrPassword] = useState('');
    const [err_confirm_password, setErrConfirmPassword] = useState('');

    const [requestStatus, setRequestStatus] = useState('success');
    const [isCaptcha, setIsCaptcha] = useState(false);
    const [eulaShow, setEulaShow] = useState(false);
    const [ppShow, setPpShow] = useState(false);
    const [promoShow, setPromoShow] = useState(false);
    const navigate = useNavigate();
    const { signUp } = useSession();

    function onCaptchaChange(value: string) {
        setCaptchaToken(value);
        setIsCaptcha(true);
    }
    function onCaptchaError() {
        setIsCaptcha(false);
    }
    function onCaptchaExpire() {
        setIsCaptcha(false);
    }
    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
        const { name, value } = event.target;
        switch (name) {
            case 'email':
                setEmail(value.toLowerCase());
                break;
            case 'forum_name':
                setForumName(value);
                break;
            case 'password':
                setPassword(value);
                break;
            case 'confirm_password':
                setConfirmPassword(value);
                break;
            case 'agree_eula':
                setAgreeEula(event.target.checked);
                break;
            case 'agree_pp':
                setAgreePp(event.target.checked);
                break;
            case 'allow_email':
                setAllowEmail(event.target.checked);
                break;
            default:
                break;
        }
    }

    const checkEmailDuplication = () => {
        console.log('checkEmailDuplication', email);
        if (email === '') {
            return;
        }

        if (email.length < 8 || email.length > 40) {
            setErrEmail('O email deve ter entre 8 e 40 caracteres.');
            return;
        }

        fetch(`${BASE_URL}auth/check_email`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email: email }),
        })
            .then((response) => response.json())
            .then((data) => {
                let err_msg = '';
                if (data.res_code !== 'SUCCESS') {
                    switch (data.res_msg) {
                        case 'ALREADY_EXISTS':
                            err_msg = 'Email has already been registered.';
                            break;
                        case 'NOT_ALLOW_VENDOR':
                            err_msg =
                                'Please use a valid email address to create an account.';
                            break;
                        case 'BAD_PARAMETER':
                            err_msg = 'Missing or invalid parameter.';
                            break;
                        default:
                            err_msg = data.res_msg;
                            break;
                    }
                }
                setErrEmail(err_msg);
            })
            .catch((error) => {
                console.error('Error:', error);
            });
    };

    const checkNicknameDuplication = () => {
        if (forum_name === '') {
            return;
        }
        fetch(`${BASE_URL}auth/check_nickname`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ nickname: forum_name }),
        })
            .then((response) => response.json())
            .then((data) => {
                let err_msg = '';
                if (data.res_code !== 'SUCCESS') {
                    if (data.res_msg === 'ALREADY_EXISTS') {
                        err_msg = 'Nickname has already been registered.';
                    } else {
                        err_msg = data.res_msg;
                    }
                }
                setErrForumName(err_msg);
            })
            .catch((error) => {
                console.error('Error:', error);
            });
    };

    const checkCountryCode = () => {
        fetch(`${BASE_URL}auth/check_valid_country`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((response) => response.json())
            .then((data) => {
                let err_msg = '';
                if (data.res_code !== 'SUCCESS') {
                    if (data.res_code === 'NOT_ALLOW_COUNTRY') {
                        alert('Unable to create an account at your location.');
                    } else {
                        alert(data.res_msg);
                    }
                    navigate('/');
                }
            })
            .catch((error) => {
                console.error('Error:', error);
            });
    };

    const isReadyToSubmit = () => {
        // Check if there are any error messages
        if (
            err_email !== '' ||
            err_forum_name !== '' ||
            err_password !== '' ||
            err_confirm_password !== ''
        ) {
            return false;
        }

        // Check if all required fields are filled
        if (
            email === '' ||
            forum_name === '' ||
            password === '' ||
            confirm_password === ''
        ) {
            return false;
        }

        // Check if passwords match
        if (password !== confirm_password) {
            return false;
        }

        // Check if terms are agreed to
        if (!agree_eula || !agree_pp) {
            return false;
        }

        // Check if captcha is completed
        if (!isCaptcha) {
            return false;
        }

        return true;
    };

    const validateForm = () => {
        if (email.length < 8 || email.length > 40) {
            setErrEmail('O email deve ter entre 8 e 40 caracteres.');
        } else {
            setErrEmail('');
        }

        if (password !== '' || confirm_password !== '') {
            if (!isPasswordValid(password)) {
                setErrPassword(
                    'Senha deve conter entre 8 e 16 letras maiúsculas e minúsculas, números e caracteres especiais.'
                );
            } else {
                setErrPassword('');
                if (password !== confirm_password) {
                    setErrConfirmPassword(
                        'Senha e Confirmar Senha não correspondem.'
                    );
                } else {
                    setErrConfirmPassword('');
                }
            }
        }
        if (forum_name !== '') {
            if (forum_name.length < 8 || forum_name.length > 50) {
                setErrForumName('Apelido deve ter entre 8 e 50 caracteres.');
            } else {
                setErrForumName('');
            }
        }
        // isReadyToSubmit();
    };

    const handleSubmit = () => {
        setRequestStatus('loading');
        validateForm();

        let params: SignUpCredentials = {
            email: email,
            nickname: forum_name,
            password: password,
            passwordConfirm: confirm_password,
            allowNewsletter: allow_email ? 'Y' : 'N',
            captcha: captchaToken,
        };

        signUp(params).then((data) => {
            let res_data = data as SignUpResponse;
            if (res_data.res_code === 'SUCCESS') {
                //success, move to valid code page
                navigate('/valid_code');
            } else {
                //fail
                if (res_data.res_msg === 'ALREADY_EXISTS_EMAIL') {
                    // Email has already been registered.
                    alert('Email já cadastrado.');
                } else if (res_data.res_msg === 'ALREADY_EXISTS_NICKNAME') {
                    // Nickname has already been registered.
                    alert('Apelido já cadastrado.');
                } else if (res_data.res_msg === 'BAD_PARAMETER') {
                    // Missing or invalid parameter.
                    alert('Parâmetro faltando ou inválido.');
                } else if (res_data.res_msg === 'NOT_ALLOW_VENDOR') {
                    // Please use a valid email address to create an account.
                    alert(
                        'Por favor, use um endereço de e-mail válido para criar uma conta.'
                    );
                } else {
                    // Fail to register. Please try again.
                    alert('Falha ao registrar. Por favor, tente novamente.');
                }
            }

            setRequestStatus('success');
        });
    };

    useEffect(() => {
        //check country
        checkCountryCode();
    }, []);

    return (
        <div className='sub_content'>
            <div className='login_box'>
                <div className='input_container'>
                    <AppInputText
                        label='* Email (obrigatório)'
                        type='email'
                        id='email'
                        value={email}
                        placeholder='Email'
                        disabled={requestStatus === 'loading'}
                        onChange={handleChange}
                        onBlur={checkEmailDuplication}
                        maxLength={40}
                    />
                    <br />
                    <ul style={{ paddingLeft: 50, textAlign: 'left' }}>
                        <li>Apenas letras minúsculas são permitidas.</li>
                        {err_email && (
                            <li style={{ color: 'red' }}>{err_email}</li>
                        )}
                    </ul>
                </div>
                <div className='input_container'>
                    <AppInputText
                        label='* Apelido (obrigatório)'
                        type='forum_name'
                        id='forum_name'
                        value={forum_name}
                        placeholder='Apelido'
                        disabled={requestStatus === 'loading'}
                        onChange={handleChange}
                        onBlur={checkNicknameDuplication}
                    />
                    <br />
                    <ul style={{ paddingLeft: 50, textAlign: 'left' }}>
                        <li>Deve conter entre 8 e 50 caracteres.</li>
                        {err_forum_name && (
                            <li style={{ color: 'red' }}>{err_forum_name}</li>
                        )}
                    </ul>
                </div>
                <div className='input_container'>
                    <AppInputText
                        label='* Senha (obrigatório)'
                        type='password'
                        id='password'
                        value={password}
                        placeholder='Senha'
                        disabled={requestStatus === 'loading'}
                        maxLength={16}
                        onChange={handleChange}
                    />
                    <br />
                    <ul style={{ paddingLeft: 50, textAlign: 'left' }}>
                        <li>
                            Use de 8 a 16 letras maiúsculas e minúsculas,
                            números e caracteres especiais.
                        </li>
                        <li>
                            Caracteres especiais permitidos: !@#$%^&*()+=~_-
                        </li>
                        {err_password && (
                            <li style={{ color: 'red' }}>{err_password}</li>
                        )}
                    </ul>
                </div>
                <div className='input_container'>
                    <AppInputText
                        label='* Confirmar Senha (obrigatório)'
                        type='password'
                        inputClassName='login_input'
                        id='confirm_password'
                        value={confirm_password}
                        placeholder='Confirmar Senha'
                        disabled={requestStatus === 'loading'}
                        maxLength={16}
                        onChange={handleChange}
                    />
                    <br />
                    <ul style={{ paddingLeft: 50, textAlign: 'left' }}>
                        {err_confirm_password && (
                            <li style={{ color: 'red' }}>
                                {err_confirm_password}
                            </li>
                        )}
                    </ul>
                </div>
                <div style={{ textAlign: 'left', paddingLeft: 20 }}>
                    <div style={{ padding: '5px 0' }}>
                        <AppInputCheckbox
                            id='agree_eula'
                            name='agree_eula'
                            onChange={handleChange}
                            checked={agree_eula}
                            label='Concordo com o EULA e os Termos de Serviço'
                            labelStyle={{ cursor: 'pointer' }}
                        />
                        <div
                            style={{
                                float: 'right',
                                paddingRight: 30,
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                setEulaShow(true);
                            }}
                        >
                            <strong>[Obrigatório]</strong>
                        </div>
                    </div>
                    <div style={{ padding: '5px 0' }}>
                        <AppInputCheckbox
                            id='agree_pp'
                            name='agree_pp'
                            onChange={handleChange}
                            checked={agree_pp}
                            label='Concordo com a Política de Privacidade'
                            labelStyle={{ cursor: 'pointer' }}
                        />
                        <div
                            style={{
                                float: 'right',
                                paddingRight: 30,
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                setPpShow(true);
                            }}
                        >
                            <strong>[Obrigatório]</strong>
                        </div>
                    </div>
                    <div style={{ padding: '5px 0' }}>
                        <AppInputCheckbox
                            id='allow_email'
                            name='allow_email'
                            onChange={handleChange}
                            checked={allow_email}
                            label='Desejo receber newsletters por e-mail'
                            labelStyle={{ cursor: 'pointer' }}
                        />
                        <div
                            style={{
                                float: 'right',
                                paddingRight: 30,
                            }}
                        >
                            <strong>[Opcional]</strong>
                        </div>
                    </div>
                </div>
                <div style={{ width: '100%', height: 20 }}></div>

                <AppTurnstile
                    id='cf-turnstile-register'
                    onCaptchaChange={onCaptchaChange}
                    onCaptchaError={onCaptchaError}
                    onCaptchaExpire={onCaptchaExpire}
                />

                <div
                    style={{
                        width: '100%',
                        height: 100,
                        textAlign: 'center',
                        marginTop: '20px',
                    }}
                >
                    <button
                        id='btnSignup'
                        type='submit'
                        disabled={
                            requestStatus === 'loading' || !isReadyToSubmit()
                        }
                        onClick={handleSubmit}
                    >
                        {requestStatus === 'loading'
                            ? 'Loading...'
                            : 'Criar Conta'}
                    </button>
                </div>
            </div>
            <EulaModal isShow={eulaShow} setShow={setEulaShow} />
            <PrivatePolicyModal isShow={ppShow} setShow={setPpShow} />
            {/* <AgreePromotionModal isShow={promoShow} setShow={setPromoShow} /> */}
        </div>
    );
};

export default RegistAccountBody;
